import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, X, ExternalLink } from "lucide-react";
import { fetchSettings } from "../lib/api";

const POLL_MS = 45000;
const DISMISS_KEY = "lovegenome_live_dismissed";

/**
 * LiveBanner — a site-wide slide-down banner that appears when the admin
 * toggles a Live Author Moment on. Polls /api/settings periodically so
 * readers see the moment go live even without a page refresh.
 */
export const LiveBanner = () => {
  const [live, setLive] = useState(null);
  const [dismissedId, setDismissedId] = useState(() =>
    typeof window !== "undefined" ? window.localStorage.getItem(DISMISS_KEY) : null,
  );

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const s = await fetchSettings();
        if (!stop) setLive(s?.live || null);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const active = !!(live?.active && live?.started_at);
  const liveId = active ? `${live.started_at}` : "";
  const dismissed = dismissedId && dismissedId === liveId;
  const show = active && !dismissed;

  const onDismiss = () => {
    setDismissedId(liveId);
    try { window.localStorage.setItem(DISMISS_KEY, liveId); } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="live-banner"
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
          className="fixed top-20 left-0 right-0 z-[45] bg-gradient-to-r from-emerald via-emerald-deep to-emerald text-cream border-b border-gold/40"
          style={{ background: "linear-gradient(90deg,#0A2416 0%,#0F3821 50%,#0A2416 100%)" }}
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 flex items-center gap-3 md:gap-5">
            <div className="flex items-center gap-2 shrink-0" data-testid="live-badge">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
              </span>
              <span className="font-body text-[10px] md:text-xs uppercase tracking-[0.28em] text-gold">
                Live Now
              </span>
            </div>

            <div className="flex-1 min-w-0 flex items-center gap-3">
              <Radio className="w-4 h-4 text-gold shrink-0 hidden md:block" />
              <div className="min-w-0">
                <p className="font-display text-sm md:text-base text-cream leading-tight truncate"
                   data-testid="live-title">
                  {live.title || "Felicia has just gone live."}
                </p>
                {live.message && (
                  <p className="hidden md:block font-body text-xs text-cream/70 truncate"
                     data-testid="live-message">
                    {live.message}
                  </p>
                )}
              </div>
            </div>

            {live.url && (
              <a
                href={live.url}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 inline-flex items-center gap-1.5 bg-gold text-ink font-body text-xs md:text-sm font-medium px-3 md:px-5 py-2 hover:bg-cream transition-colors"
                data-testid="live-join-btn"
              >
                Join Now <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}

            <button
              onClick={onDismiss}
              aria-label="Dismiss live banner"
              data-testid="live-dismiss-btn"
              className="shrink-0 text-cream/50 hover:text-cream p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LiveBanner;
